import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";

const PRESET_COLORS = ["#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899", "#14B8A6"];

export default function AppointmentTypesPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("all");

  const load = async () => {
    try { const { data } = await api.get("/appointment-types"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing({
      name: "", duration_min: 60, color: PRESET_COLORS[0], price: 0, notes: "",
      calendar: tab === "trainers" ? "trainers" : "consulenti",
    });
    setOpen(true);
  };
  const openEdit = (t) => { setEditing({ ...t }); setOpen(true); };

  const save = async () => {
    try {
      const body = {
        name: editing.name,
        duration_min: Number(editing.duration_min),
        color: editing.color,
        price: Number(editing.price || 0),
        notes: editing.notes || "",
        calendar: editing.calendar || "consulenti",
      };
      if (editing.id) await api.patch(`/appointment-types/${editing.id}`, body);
      else await api.post("/appointment-types", body);
      toast.success("Salvato"); load(); setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Eliminare "${t.name}"?`)) return;
    try { await api.delete(`/appointment-types/${t.id}`); load(); toast.success("Eliminato"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = tab === "all" ? items : items.filter((t) => (t.calendar || "consulenti") === tab);
  const counts = {
    consulenti: items.filter((t) => (t.calendar || "consulenti") === "consulenti").length,
    trainers: items.filter((t) => t.calendar === "trainers").length,
  };

  return (
    <div className="p-8 h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Tipi di Appuntamento</h1>
          <p className="text-muted-foreground text-sm mt-1">Configura servizi, durate, colori e prezzi — separati per Consulenti e Trainers.</p>
        </div>
        <Button onClick={openNew} data-testid="add-appointment-type-button"><Plus className="h-4 w-4 mr-2" />Nuovo Tipo</Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="apt-types-tab-all">Tutti · {items.length}</TabsTrigger>
          <TabsTrigger value="consulenti" data-testid="apt-types-tab-consulenti">Consulenti · {counts.consulenti}</TabsTrigger>
          <TabsTrigger value="trainers" data-testid="apt-types-tab-trainers">Trainers · {counts.trainers}</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="bg-card border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Calendario</TableHead>
              <TableHead>Durata</TableHead>
              <TableHead>Prezzo (€)</TableHead>
              <TableHead>Colore</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((t) => (
              <TableRow key={t.id} data-testid={`appointment-type-row-${t.id}`}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell>
                  <Badge variant={t.calendar === "trainers" ? "default" : "secondary"} className={t.calendar === "trainers" ? "bg-purple-600" : ""}>
                    {t.calendar === "trainers" ? "Trainers" : "Consulenti"}
                  </Badge>
                </TableCell>
                <TableCell>{t.duration_min} min</TableCell>
                <TableCell>€ {Number(t.price || 0).toFixed(2)}</TableCell>
                <TableCell><span className="inline-block h-4 w-4 rounded-full" style={{ backgroundColor: t.color }} /></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(t)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(t)} className="text-rose-600"><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing?.id ? "Modifica" : "Nuovo"} Tipo</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Calendario</Label>
                <Tabs value={editing.calendar || "consulenti"} onValueChange={(v) => setEditing({ ...editing, calendar: v })}>
                  <TabsList className="grid grid-cols-2 w-full">
                    <TabsTrigger value="consulenti" data-testid="type-calendar-consulenti">Consulenti</TabsTrigger>
                    <TabsTrigger value="trainers" data-testid="type-calendar-trainers">Trainers</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <div><Label>Nome</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="type-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Durata (min)</Label><Input type="number" value={editing.duration_min} onChange={(e) => setEditing({ ...editing, duration_min: e.target.value })} /></div>
                <div><Label>Prezzo (€)</Label><Input type="number" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} /></div>
              </div>
              <div>
                <Label>Colore</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {PRESET_COLORS.map((c) => (
                    <button key={c} onClick={() => setEditing({ ...editing, color: c })}
                      className={`h-8 w-8 rounded-full ${editing.color === c ? "ring-2 ring-offset-2 ring-slate-900" : ""}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
              <div><Label>Note</Label><Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-type-button">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
